import {
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptor,
  HttpResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Injector, Injectable, inject } from '@angular/core';
import { ToastService } from '../shared/toast/toast.service';
import { Router } from '@angular/router';
import { CommonService } from '../services/common.service';
import { AppUpdateService } from '../services/app-update.service';

export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorIntercept implements HttpInterceptor {
  private injector = inject(Injector);
  private router = inject(Router);
  private commonService = inject(CommonService);
  private appUpdateService = inject(AppUpdateService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const toasterService = this.injector.get(ToastService);
    const smtxVersion: number = 20261013; // yyyymmdd
    return next.handle(request).pipe(
      tap(response => {
        if (
          response instanceof HttpResponse &&
          response.headers &&
          response.headers.get('smtx-version') &&
          !(smtxVersion >= parseInt(response.headers.get('smtx-version')!))
        ) {
          // MP-5600: Delegate version reload to AppUpdateService so checkouts
          // and active in-memory cart states are never interrupted.
          this.appUpdateService.handleBackendVersionMismatch();
        }
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (request.context.get(SUPPRESS_ERROR_TOAST)) {
          return throwError(errorResponse);
        }
        if (errorResponse instanceof HttpErrorResponse && errorResponse.error) {
          if (errorResponse.error['exception-handling-request']) {
            toasterService.showErrorMsg('An unknown error occurred');
          } else if (typeof errorResponse.error === 'string') {
            toasterService.showErrorMsg(errorResponse.error);
          } else if (
            errorResponse.error.message &&
            errorResponse.error.message != null &&
            (Array.isArray(errorResponse.error.message) ? errorResponse.error.message.length > 0 : typeof errorResponse.error.message === 'string')
          ) {
            const rawMessage = errorResponse.error.message;
            const message = Array.isArray(rawMessage) ? rawMessage[0] : (typeof rawMessage === 'string' ? rawMessage : undefined);
            const htmlTagPattern = /<[^>]+>/;
            if (message && htmlTagPattern.test(message)) {
              this.commonService.showNotification(
                message,
                'error'
              );
            } else if (message) {
              toasterService.showErrorMsg(message);
            }
          } else if (
            errorResponse.error &&
            (errorResponse.error.statusCode == 406 || errorResponse.error.statusCode == 400)
          ) {
            const isIngoreErrorMessage =
              errorResponse.message &&
                errorResponse.error &&
                errorResponse.error.errorMessages &&
                errorResponse.error.errorMessages.length &&
                errorResponse.error.errorMessages[0] &&
                this.skipAPIForErrorMessage(errorResponse.url) &&
                this.skipErrorMessage(errorResponse.error.errorMessages[0])
                ? true
                : false;

            if (!isIngoreErrorMessage)
              toasterService.showErrors(errorResponse.error);
          } else if (
            this.isPromoCodeRateLimitApi(request?.url || errorResponse?.url) &&
            (errorResponse.status === 429 || errorResponse.error?.statusCode === 429)
          ) {
            if (
              errorResponse.error &&
              errorResponse.error.errorMessages &&
              errorResponse.error.errorMessages.length > 0
            ) {
              toasterService.showErrors(errorResponse.error);
            } else {
              toasterService.showErrorMsg(
                errorResponse.error?.message || 'Too many attempts. Please try again later.'
              );
            }
          }
        } else if (errorResponse.status == 403) {
          this.router.navigate(['/404']);
        } else if (
          this.isPromoCodeRateLimitApi(request?.url || errorResponse?.url) &&
          errorResponse.status === 429
        ) {
          toasterService.showErrorMsg('Too many attempts. Please try again later.');
        } else {
          toasterService.showErrorMsg('An unknown error occurred');
        }
        return throwError(errorResponse);
      })
    );
  }

  private isPromoCodeRateLimitApi(apiName?: string): boolean {
    try {
      if (!apiName) {
        return false;
      }
      const lower = apiName.toLowerCase();
      return (
        lower.includes('promocodeapplyorremoveonsections') ||
        lower.includes('applyremovepromocodeoncheckoutpage')
      );
    } catch (err) {
      console.error('Error in isPromoCodeRateLimitApi:', err);
      return false;
    }
  }

  private skipErrorMessage(errorMessage: string): boolean {
    try {
      return (
        [
          'Invalid OrderID. Order Not Found.',
          'Invalid eventID. Event Not Found.',
          'Bad request, There is no bundle deal found in your order',
          'Invalid OpenTicketSectionId. Open Ticket Section Not Found.',
        ].filter(r => r === errorMessage).length > 0
      );
    } catch (err) {
      console.error('Error in skipErrorMessage:', err);
      return false;
    }
  }

  private skipAPIForErrorMessage(apiName: string): boolean {
    try {
      return (
        ['GetBundleDealSections'].filter(r => apiName.includes(r)).length > 0
      );
    } catch (err) {
      console.error('Error in skipAPIForErrorMessage:', err);
      return false;
    }
  }
}
